import type { Tutorial } from '../../shared/types.js';

export const introTutorial: Tutorial = {
  name: 'intro',
  title: 'Introduction to Reflex',
  description:
    "A quick tour of the Reflex sidebar and the major components you'll use day to day: agents, search, your inbox, the account menu, the org switcher, and Configure.",
  category: 'getting-started',
  docPages: ['overview', 'get-started'],
  agenda: [
    'Sidebar navigation and search',
    'Your account menu',
    'Switching organizations',
    'Configure',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'Welcome to Reflex',
      body: 'Reflex lets you **launch and monitor background coding agents**. This short tour points out the navigation and the components you will use most. Use Next to step through, or close the tour any time.',
      path: '/runs',
    },
    {
      id: 'agents',
      kind: 'info',
      title: 'Sessions',
      body: 'This is your **home base**. The Sessions view lists every agent you have launched with live status, and is where you start new ones. The sidebar entry takes you back here from anywhere.',
      target: '[data-tutorial-id="nav-agents"]',
      placement: 'right',
    },
    {
      id: 'search',
      kind: 'info',
      title: 'Search and command palette',
      body: 'Open the command palette to jump to any page, agent, or resource. You can also press [[mod+K]] from anywhere.',
      target: '[aria-label="Search"]',
      placement: 'right',
    },
    {
      id: 'inbox',
      kind: 'info',
      title: 'Inbox',
      body: 'Your inbox surfaces notifications and items that need your attention. This includes agents waiting on input, broadcasts, and more.',
      target: '[data-testid="user-section-inbox"]',
      placement: 'right',
    },
    {
      id: 'account-menu',
      kind: 'action',
      trigger: 'click',
      title: 'Your account menu',
      body: 'Your profile sits in the bottom-left corner. Click the profile picture button to open your account menu. The tour will continue once the profile is open.',
      target: '[aria-label="Account menu"]',
      placement: 'right',
      // The avatar opens a Radix DropdownMenu: it opens on pointerdown and the
      // portaled content immediately covers the trigger, so no `click` ever
      // lands on it for the engine to observe - advance the moment the menu
      // content appears instead (matches the agent-detail actions-menu step).
      advanceWhen: '[data-tutorial-id="user-menu-content"]',
    },
    {
      id: 'menu-account',
      kind: 'info',
      title: 'Account',
      body: '**Account** opens your personal area: your profile, your settings (theme, defaults, connected accounts), the **Tutorials** library where you can replay this tour, and anything else tied to you rather than to an organization.',
      target: '[data-tutorial-id="user-menu-item-account"]',
      placement: 'right',
    },
    {
      id: 'menu-notifications',
      kind: 'info',
      title: 'Notifications',
      body: '**Notifications** is your feed of product and operational updates. The badge here shows how many are unread.',
      target: '[data-tutorial-id="user-menu-item-notifications"]',
      placement: 'right',
    },
    {
      id: 'org-switcher',
      kind: 'info',
      title: 'Organization switcher',
      body: 'Reflex supports multiple organizations. Use the org switcher to move between them. **Credentials, integrations, and agents are all scoped to the active organization.**',
      target: '[data-testid="org-switcher-trigger"]',
      placement: 'bottom',
      // The account menu is a modal Radix dropdown anchored in the bottom-left;
      // leaving it open would cover the sidebar and block the spotlight up at
      // the org switcher. Stamp a close signal on <html> as this step opens -
      // UserSection observes it (see its effect) and closes the menu. Kept
      // boundary-free: a DOM signal rather than importing the tutorial plugin.
      htmlSignals: { 'data-tutorial-close-user-menu': 'true' },
    },
    {
      id: 'settings-entry',
      kind: 'info',
      title: 'Configure',
      body: '**Configure** is where organization setup lives: **members, teams, environments, and agents**. Integrations and credentials live in the separate **Connect** surface. Click the gear to step in; press [[Escape]] or Back to return to your work.',
      target: '[data-tutorial-id="nav-settings"]',
      placement: 'right',
    },
    {
      id: 'done',
      kind: 'info',
      title: "You're ready",
      body: 'That is the tour. Next, try the "Launch an agent" tutorial to start your first agent, or set up the GitHub and Slack integrations.',
    },
  ],
  handoff: { tutorialName: 'launch-agent', label: 'Launch an agent' },
  related: [
    { tutorialName: 'connect-github', label: 'Connect GitHub' },
    { tutorialName: 'connect-slack', label: 'Connect Slack' },
  ],
};
